import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  getAuth,
  User,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { firebaseApp } from '../lib/firebase';
import { ICliente, IUsuario as Usuario } from '../types/firestore.types';

const auth = getAuth(firebaseApp);
const firestore = getFirestore(firebaseApp);
const COLECAO_USUARIOS = 'usuarios';

/**
 * Cria um novo usuário no sistema TugasCoins com email e senha,
 * e armazena seus dados essenciais no Firestore.
 *
 * @param email O endereço de email do novo usuário.
 * @param senha A senha do novo usuário.
 * @param nome O nome completo do novo usuário.
 * @param tipoUsuario O tipo de usuário (ex: 'cliente', 'operador', 'administrador').
 * @param unidadeId O ID da unidade à qual o usuário pertence (opcional).
 * @returns Uma Promise que resolve com o objeto Usuario criado.
 * @throws Erro se a criação do usuário ou o salvamento no Firestore falhar.
 */

export async function criarUsuario(
  email: string,
  senha: string,
  nome: string,
  telefone: string,
  tipoUsuario: Usuario['tipo_usuario'],
  unidadeId: string | null = null,
  aceitou_termos: boolean,
): Promise<Usuario> {
  try {
    // 1. Cria o usuário no Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(auth, email, senha);
    const firebaseUser = userCredential.user;

    // 2. Prepara o objeto do novo usuário. Ele será usado em ambas as coleções.
    const novoUsuario: Usuario = {
      id: firebaseUser.uid,
      nome: nome,
      email: email,
      telefone: telefone,
      tipo_usuario: tipoUsuario,
      unidade_id: unidadeId,
      aceitou_termos: aceitou_termos,
      criado_em: serverTimestamp() as Timestamp, // O SDK vai converter isso para a data no servidor
      atualizado_em: serverTimestamp() as Timestamp,
    };

    // 3. Inicia um 'batch' de escrita no Firestore
    const batch = writeBatch(firestore);

    // 4. Adiciona a operação de escrita para a coleção 'usuarios' ao batch
    const usuarioDocRef = doc(firestore, COLECAO_USUARIOS, firebaseUser.uid);
    batch.set(usuarioDocRef, novoUsuario);

    // 5. Se for cliente, adiciona a operação de escrita para a coleção 'clientes' ao batch
    if (tipoUsuario === 'cliente') {
      // Reutiliza os dados relevantes para o cliente
      const clienteData: ICliente = {
        id: firebaseUser.uid,
        nome: nome,
        email: email,
        telefone: telefone,
        criado_em: novoUsuario.criado_em, // Usa a mesma instância do serverTimestamp
        atualizado_em: novoUsuario.atualizado_em,
      };
      const clienteDocRef = doc(firestore, 'clientes', firebaseUser.uid);
      batch.set(clienteDocRef, clienteData);
    }

    // 6. Executa (commita) todas as operações no batch atomicamente.
    // É neste ponto que os dados são realmente enviados para o Firebase.
    // Se qualquer operação falhar, todas são revertidas.
    await batch.commit();

    // 7. Retorna o objeto que foi salvo. Não é necessário ler do banco novamente.
    // O objeto retornado não terá a data exata do servidor (terá o objeto sentinel),
    // mas para a UI logo após o cadastro, isso raramente é um problema.
    return novoUsuario;

  } catch (error: any) {
    // Aqui você pode tratar erros específicos do Firebase para dar feedback melhor ao usuário
    // Ex: if (error.code === 'auth/email-already-in-use') { ... }
    throw new Error(`Falha ao registrar usuário: ${error.message}`);
  }
}

/**
 * Realiza o login de um usuário existente com email e senha.
 * Após a autenticação, busca os dados completos do usuário no Firestore.
 *
 * @param email O endereço de email do usuário.
 * @param senha A senha do usuário.
 * @returns Uma Promise que resolve com o objeto Usuario logado.
 * @throws Erro se o login falhar ou se os dados do usuário não forem encontrados no Firestore.
 */
export async function loginUsuario(email: string, senha: string): Promise<Usuario> {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, senha);
    const firebaseUser = userCredential.user;

    const usuarioDocRef = doc(firestore, COLECAO_USUARIOS, firebaseUser.uid);
    const usuarioDoc = await getDoc(usuarioDocRef);

    if (!usuarioDoc.exists()) {
      throw new Error('Dados do usuário não encontrados, após o login.');
    }

    const usuarioData = usuarioDoc.data() as Usuario;
    return {
      ...usuarioData,
      id: usuarioDoc.id,
      criado_em: usuarioData.criado_em instanceof Timestamp ? usuarioData.criado_em : Timestamp.fromDate(new Date()),
      atualizado_em: usuarioData.atualizado_em instanceof Timestamp ? usuarioData.atualizado_em : Timestamp.fromDate(new Date()),
    };
  } catch (error: any) {
    // Antes, TODO erro virava 'Usuário não encontrado.' — senha errada, documento
    // ausente, permissão negada e rede fora ficavam indistinguíveis, e não havia
    // como diagnosticar uma falha de login. O código do Firebase vai para o
    // console; a tela recebe uma frase que diz de fato o que aconteceu.
    console.error('[login] falha:', error?.code, error?.message, error);

    switch (error?.code) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        throw new Error('E-mail ou senha incorretos.');
      case 'auth/invalid-email':
        throw new Error('E-mail em formato inválido.');
      case 'auth/user-disabled':
        throw new Error('Esta conta está desativada no Firebase Authentication.');
      case 'auth/too-many-requests':
        throw new Error('Bloqueado temporariamente por excesso de tentativas. Aguarde alguns minutos ou redefina a senha por e-mail.');
      case 'auth/network-request-failed':
        throw new Error('Sem conexão com o Firebase. Verifique a rede.');
      case 'permission-denied':
        throw new Error('Login autenticado, mas as regras do Firestore negaram a leitura do seu cadastro.');
      default:
        // Erros que não são do Auth (ex.: documento ausente) chegam com a
        // própria mensagem, que já é específica.
        throw new Error(error?.message || 'Falha ao entrar. Tente novamente.');
    }
  }
}

/**
 * Realiza o logout do usuário atualmente autenticado.
 *
 * @returns Uma Promise que resolve quando o logout é concluído.
 * @throws Erro se o logout falhar.
 */
export async function logoutUsuario(): Promise<void> {
  try {
    await signOut(auth);
  } catch (error: any) {
    throw new Error(`Falha ao sair da sessão: ${error.message}`);
  }
}

/**
 * Observa as mudanças no estado de autenticação do usuário.
 * A função de callback é invocada sempre que o estado de autenticação muda,
 * fornecendo o objeto Usuario completo ou null se não houver usuário autenticado.
 *
 * @param callback Uma função que recebe o objeto Usuario ou null.
 * @returns Uma função de unsubscribe para parar de observar as mudanças.
 */
export function observarEstadoAutenticacao(
  callback: (usuario: Usuario | null) => void
): () => void {
  const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      try {
        const usuarioDocRef = doc(firestore, COLECAO_USUARIOS, firebaseUser.uid);
        const usuarioDoc = await getDoc(usuarioDocRef);

        if (usuarioDoc.exists()) {
          const usuarioData = usuarioDoc.data() as Usuario;

          callback({
            ...usuarioData,
            id: usuarioDoc.id,
            criado_em: usuarioData.criado_em instanceof Timestamp ? usuarioData.criado_em : Timestamp.fromDate(new Date()),
            atualizado_em: usuarioData.atualizado_em instanceof Timestamp ? usuarioData.atualizado_em : Timestamp.fromDate(new Date()),
          });
        } else {
          callback(null);
        }
      } catch (error) {
        callback(null);
      }
    } else {
      callback(null);
    }
  });

  return unsubscribe;
}